import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinicPayStatus, useStartClinicPayOnboarding, deriveClinicPayDisplayStatus } from '@/hooks/useClinicPay';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { IntegrationStatusBanner } from '@/components/IntegrationStatusBanner';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';

export function ClinicPayments() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: payStatus, isLoading, error } = useClinicPayStatus(activeClinicId);
  const onboard = useStartClinicPayOnboarding(activeClinicId);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  async function handleStartOnboarding() {
    if (!activeClinicId) return;
    try {
      await onboard.mutateAsync(undefined);
    } catch (err) {
      console.error('pay onboarding failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('payments.pay_onboarding_error');
      toast.error(message);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) {
    return <ErrorAlert message={t('payments.pay_status_error')} />;
  }

  const status = deriveClinicPayDisplayStatus(payStatus);
  const isPending   = !payStatus || status === 'pending';
  const isOnboarding = status === 'onboarding';
  const isActive    = status === 'active';
  const isRestricted = status === 'restricted';
  const isDisabled  = status === 'disabled';

  return (
    <div className="space-y-6">
      <IntegrationsBackLink />

      {/* Clinio Pay service availability banner (SHARED-5) */}
      <IntegrationStatusBanner integration="pay" />

      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('settings.integrations.cards.pay_title')}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('payments.subtitle')}
        </p>
      </div>

      {/* Test mode banner (PAY-1 ADD §7 — read-only, yellow banner) */}
      {payStatus?.test_mode && (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-700 dark:bg-yellow-900/30">
          <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400" />
          <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
            {t('payments.test_mode_active')}
          </span>
        </div>
      )}

      {/* Clinio Pay status card */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="mb-4 flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-indigo-500" />
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            {t('payments.clinio_pay_title')}
          </h3>
          {payStatus && (
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}>
              {t(`payments.status_${status}`)}
            </span>
          )}
        </div>

        {/* ── State: not yet provisioned / pending ─────────────────────────── */}
        {isPending && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('payments.not_connected_desc')}
            </p>
            {canAdmin && (
              <Button onClick={handleStartOnboarding} disabled={onboard.isPending}>
                {onboard.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                {t('payments.activate_pay')}
              </Button>
            )}
          </div>
        )}

        {/* ── State: onboarding (Stripe KYC incomplete) ────────────────────── */}
        {isOnboarding && payStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-700 dark:bg-yellow-900/30">
              <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm text-yellow-800 dark:text-yellow-300">
                {t('payments.onboarding_incomplete')}
              </span>
            </div>
            {payStatus.stripe_account_id && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('payments.account_id')}:{' '}
                <span className="font-mono">{payStatus.stripe_account_id.slice(0, 12)}…</span>
              </p>
            )}
            {canAdmin && (
              <Button
                onClick={handleStartOnboarding}
                disabled={onboard.isPending}
                variant="secondary"
              >
                {onboard.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t('payments.resume_onboarding')}
              </Button>
            )}
          </div>
        )}

        {/* ── State: active ─────────────────────────────────────────────────── */}
        {isActive && payStatus && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{t('payments.status_active')}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {payStatus.stripe_account_id && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('payments.account_id')}: </span>
                  <span className="font-mono text-gray-900 dark:text-white">
                    {payStatus.stripe_account_id.slice(0, 12)}…
                  </span>
                </div>
              )}
              {payStatus.last_synced_at && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('payments.last_synced')}: </span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(payStatus.last_synced_at).toLocaleString('da-DK')}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('payments.platform_fee_percent_label')}: </span>
                <span className="text-gray-900 dark:text-white">
                  {payStatus.platform_fee_percent} %
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('payments.charges_enabled')}: </span>
                <span className={payStatus.charges_enabled ? 'text-emerald-600' : 'text-gray-500'}>
                  {payStatus.charges_enabled ? t('common.yes') : t('common.no')}
                </span>
              </div>
            </div>

            {payStatus.stripe_account_id && (
              <a
                href={`https://dashboard.stripe.com/${payStatus.stripe_account_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
              >
                {t('payments.dashboard_link')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        {/* ── State: restricted ─────────────────────────────────────────────── */}
        {isRestricted && payStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-900/30">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-800 dark:text-red-300">
                {t('payments.stripe_restricted')}
              </span>
            </div>
            {payStatus.stripe_account_id && (
              <a
                href={`https://dashboard.stripe.com/${payStatus.stripe_account_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
              >
                {t('payments.dashboard_link')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        {/* ── State: disabled ───────────────────────────────────────────────── */}
        {isDisabled && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('payments.status_rejected_desc')}
          </p>
        )}
      </section>

      {/* Platform fee info (read-only — source of truth is Pay) */}
      {payStatus && isActive && (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
            {t('payments.platform_fee_title')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {payStatus.platform_fee_percent} %
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('payments.platform_fee_desc')}
          </p>
        </section>
      )}

      {/* Webhook endpoint info */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <h3 className="mb-3 text-base font-medium text-gray-900 dark:text-white">
          {t('payments.webhook_title')}
        </h3>
        <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          {t('payments.clinio_pay_webhook_desc')}
        </p>
        <div className="rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          https://pupgbtuzlamtircfdamp.supabase.co/functions/v1/pay-webhook-receiver
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {t('payments.clinio_pay_webhook_note')}
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string | null): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    case 'onboarding':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'restricted':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'disabled':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  }
}
